import React, { useState, useMemo, useEffect } from 'react';
import type {
  MergeSuggestion,
  MergeSuggestionsResponse,
  MergePreview,
  MergeRequest,
  MergeResponse,
} from '../../services/api/domains/merge/types';
import { modalStyles } from './styles';
import { useModalBackDismiss } from 'navigation/useModalBackDismiss';
import { DEBOUNCE_DELAY } from 'shared/hooks/useAsyncSelect';
import useAuth from '../../auth/auth';
import type { ApiState } from '../../services/api/types';
import LgmButton from '../LgmButton';
import {
  GRAY_100,
  GRAY_200,
  GRAY_300,
  GRAY_400,
  GRAY_500,
  GRAY_700,
} from 'theme/grayScale';
import {
  COLOR_DANGER,
  COLOR_DANGER_TEXT,
  COLOR_INFO,
  COLOR_WARNING,
  COLOR_WARNING_BG,
  COLOR_WARNING_TEXT,
} from 'theme/colors';

// Generic configuration for domain-specific behavior
export interface MergeModalConfig<T> {
  domainDisplayName: string; // "Therapeutic Area", "Product Type", "Manufacturer", etc.
  domainDisplayNamePlural: string; // "Therapeutic Areas", "Product Types", "Manufacturers", etc.
  entityIdField: keyof T;
  entityNameField: keyof T;
  entityCreatedDateField: keyof T;
  useMergeSuggestions: (
    sourceId: number | null,
    maxResults?: number
  ) => ApiState<MergeSuggestionsResponse<T>> & {
    refetch: () => Promise<void>;
    invalidate: () => void;
  };
  useMergePreview: (
    sourceId: number | null,
    targetId: number | null
  ) => ApiState<MergePreview> & {
    refetch: () => Promise<void>;
    invalidate: () => void;
  };
  useMerge: () => ApiState<MergeResponse> & {
    mutate: (variables: {
      sourceId: number;
      targetId: number;
      request: MergeRequest;
    }) => Promise<MergeResponse>;
  };
  // Optional render function for domain-specific conflict resolution
  renderConflicts?: (
    previewData: MergePreview,
    resolvedFields: Record<
      string,
      string | number | boolean | Date | null | undefined
    >,
    onFieldResolve: (
      fieldName: string,
      value: string | number | boolean | Date | null | undefined,
      source: 'source' | 'target'
    ) => void
  ) => React.ReactNode;
}

interface GenericMergeModalProps<T> {
  source: T;
  allEntities: T[];
  config: MergeModalConfig<T>;
  onClose: () => void;
  onSuccess: () => void;
  /**
   * Optional server-side search across the FULL domain, not just
   * `allEntities` (which is often a paginated/filtered page from the
   * caller's own grid — see issue #653 Task 22). When provided, a non-empty
   * search term is answered by this function instead of filtering
   * `allEntities` client-side, so a merge target outside the caller's
   * current page/filter can still be found and selected.
   */
  searchEntities?: (searchTerm: string) => Promise<T[]>;
}

type MergeStep = 'SELECT_TARGET' | 'RESOLVE_CONFLICTS' | 'CONFIRM';

function GenericMergeModal<T>({
  source: initialSource,
  allEntities,
  config,
  onClose,
  onSuccess,
  searchEntities,
}: GenericMergeModalProps<T>) {
  const { account } = useAuth();
  const [step, setStep] = useState<MergeStep>('SELECT_TARGET');
  const [source, setSource] = useState<T>(initialSource);
  const [selectedTargetId, setSelectedTargetId] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [serverSearchResults, setServerSearchResults] = useState<T[]>([]);
  const [isServerSearching, setIsServerSearching] = useState(false);
  const [selectedCanonicalName, setSelectedCanonicalName] = useState<
    'source' | 'target'
  >('target');
  const [mergeReason, setMergeReason] = useState('');
  const [showHelp, setShowHelp] = useState(false);

  useModalBackDismiss({ isOpen: true, onClose });
  const [resolvedFields, setResolvedFields] = useState<
    Record<string, string | number | boolean | Date | null | undefined>
  >({});

  const {
    domainDisplayName,
    domainDisplayNamePlural,
    entityIdField,
    entityNameField,
    entityCreatedDateField,
    useMergeSuggestions,
    useMergePreview,
    useMerge,
    renderConflicts,
  } = config;

  // Get source entity properties
  const sourceId = source[entityIdField] as number;
  const sourceName = source[entityNameField] as string;
  const sourceCreatedDate = source[entityCreatedDateField] as string;

  // Fetch merge suggestions
  const suggestionsQuery = useMergeSuggestions(sourceId);

  // Fetch merge preview when target is selected
  const previewQuery = useMergePreview(
    step === 'RESOLVE_CONFLICTS' || step === 'CONFIRM' ? sourceId : null,
    selectedTargetId
  );

  // Merge mutation
  const mergeMutation = useMerge();

  // Server-side search across the full domain (issue #653 Task 22) — debounced
  // the same way useAsyncSelect debounces its own search-as-you-type callers.
  // Skips entirely when the caller doesn't pass `searchEntities` (Grades/
  // Therapeutic Areas/Facilities pass their full in-memory list today, so
  // client-side filtering over `allEntities` below stays correct for them).
  useEffect(() => {
    if (!searchEntities) return;
    const trimmed = searchTerm.trim();
    if (!trimmed) {
      // Clearing the search cancels the previous effect's in-flight
      // request (via its own cleanup below), whose `finally` block then
      // sees `cancelled === true` and skips resetting the flag. Without
      // this, isServerSearching can stay stuck true forever after a clear.
      setServerSearchResults([]);
      setIsServerSearching(false);
      return;
    }

    let cancelled = false;
    // Clear stale results from the previous term immediately, not just on
    // completion — otherwise the modal shows the old term's matches while
    // this debounced request is in flight, and keeps showing them if the
    // request fails.
    setServerSearchResults([]);
    setIsServerSearching(true);
    const timeoutId = setTimeout(async () => {
      try {
        const results = await searchEntities(trimmed);
        if (!cancelled) setServerSearchResults(results);
      } catch (error) {
        console.error('Merge target search failed:', error);
        if (!cancelled) setServerSearchResults([]);
      } finally {
        if (!cancelled) setIsServerSearching(false);
      }
    }, DEBOUNCE_DELAY);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [searchEntities, searchTerm]);

  // Extract data safely
  const suggestionsData =
    suggestionsQuery.status === 'success' ? suggestionsQuery.data : undefined;
  const previewData =
    previewQuery.status === 'success' ? previewQuery.data : undefined;

  // Filter entities based on search. A non-empty term is answered by
  // `serverSearchResults` (already server-filtered across the full domain)
  // when `searchEntities` was provided, instead of filtering `allEntities` —
  // see the effect above and the prop's own doc comment for why.
  const filteredTargets = useMemo(() => {
    const suggestions = suggestionsData?.suggestions || [];
    const trimmedTerm = searchTerm.trim().toLowerCase();

    const availableTargets =
      searchEntities && trimmedTerm
        ? serverSearchResults.filter(
            entity => entity[entityIdField] !== sourceId
          )
        : allEntities.filter(
            entity =>
              entity[entityIdField] !== sourceId &&
              String(entity[entityNameField])
                .toLowerCase()
                .includes(trimmedTerm)
          );

    // Combine suggestions with search results, removing duplicates
    const suggestionIds = new Set(
      suggestions.map((s: MergeSuggestion<T>) => s.id)
    );
    const nonSuggestionTargets = availableTargets.filter(
      entity => !suggestionIds.has(entity[entityIdField] as number)
    );

    return {
      suggestions: suggestions.map((s: MergeSuggestion<T>) => {
        // Prefer an explicit `entity` payload, then a match in the
        // caller's (possibly-paginated) `allEntities`, then fall back to
        // the suggestion row itself — `sp_GetSimilarManufacturers` (and its
        // Grades/Therapeutic Areas equivalents) already return the entity's
        // own id/name/created-date columns directly on each suggestion row,
        // so this still renders correctly even when the suggested target
        // isn't on the caller's current page.
        const entity =
          s.entity ??
          allEntities.find(e => e[entityIdField] === s.id) ??
          (s as unknown as T);
        return { ...entity, similarity_score: s.similarity_score };
      }),
      others: nonSuggestionTargets,
    };
  }, [
    suggestionsData,
    allEntities,
    serverSearchResults,
    searchEntities,
    sourceId,
    searchTerm,
    entityIdField,
    entityNameField,
  ]);

  const selectedTarget = useMemo(() => {
    if (!selectedTargetId) return null;
    return (
      allEntities.find(entity => entity[entityIdField] === selectedTargetId) ??
      serverSearchResults.find(
        entity => entity[entityIdField] === selectedTargetId
      ) ??
      filteredTargets.suggestions.find(
        entity => entity[entityIdField] === selectedTargetId
      ) ??
      null
    );
  }, [
    selectedTargetId,
    allEntities,
    serverSearchResults,
    filteredTargets.suggestions,
    entityIdField,
  ]);

  // Auto-select target name by default
  useEffect(() => {
    if (selectedTarget) {
      setSelectedCanonicalName('target');
    }
  }, [selectedTarget]);

  const handleTargetSelect = (targetId: number) => {
    setSelectedTargetId(targetId);
    setStep('RESOLVE_CONFLICTS');
  };

  const handleBack = () => {
    if (step === 'RESOLVE_CONFLICTS') {
      setStep('SELECT_TARGET');
      setSelectedTargetId(null);
    } else if (step === 'CONFIRM') {
      setStep('RESOLVE_CONFLICTS');
    }
  };

  const handleProceedToConfirm = () => {
    setStep('CONFIRM');
  };

  const handleSwapSourceTarget = () => {
    if (!selectedTargetId || !selectedTarget) return;

    // Swap the source and target entities
    const newSource = selectedTarget;
    const newTargetId = sourceId;

    setSource(newSource);
    setSelectedTargetId(newTargetId);

    // Reset to default selection (target name)
    setSelectedCanonicalName('target');
  };

  const handleFieldResolve = (
    fieldName: string,
    value: string | number | boolean | Date | null | undefined,
    _: 'source' | 'target'
  ) => {
    setResolvedFields(prev => ({
      ...prev,
      [`resolved_${fieldName}`]: value,
    }));
  };

  const handleExecuteMerge = async () => {
    if (!selectedTargetId || !account || !selectedTarget) return;

    const canonicalName =
      selectedCanonicalName === 'source'
        ? sourceName
        : (selectedTarget[entityNameField] as string);

    try {
      await mergeMutation.mutate({
        sourceId: sourceId,
        targetId: selectedTargetId,
        request: {
          new_canonical_name: canonicalName,
          merge_reason: mergeReason || undefined,
          user_id: account.username || account.name || 'Unknown',
          // Include resolved fields for conflict resolution
          ...resolvedFields,
        },
      });

      onSuccess();
      onClose();
    } catch (error) {
      console.error('Merge failed:', error);
      alert(`Merge failed: ${error}`);
    }
  };

  return (
    <div style={modalStyles.modalOverlayStyle} onClick={onClose}>
      <div
        style={{
          ...modalStyles.modalContentStyle,
          width: '800px',
          maxWidth: '90vw',
        }}
        onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={modalStyles.modalHeaderStyle}>
          <h2 style={{ ...modalStyles.modalTitleStyle, marginBottom: 0 }}>
            Merge {domainDisplayName}: {sourceName}
          </h2>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <LgmButton
              variant="flat"
              onClick={() => setShowHelp(!showHelp)}
              style={{
                fontSize: '16px',
                padding: '4px 8px',
                color: GRAY_500,
              }}
              hoverStyle={{
                backgroundColor: GRAY_100,
                color: GRAY_700,
              }}
              pressedStyle={{
                backgroundColor: GRAY_200,
                color: GRAY_700,
              }}
              title={showHelp ? 'Hide Help' : 'Show Help'}>
              {showHelp ? '✕ Help' : 'ℹ️ Help'}
            </LgmButton>
            <LgmButton
              variant="flat"
              onClick={onClose}
              style={modalStyles.modalCloseXStyle}
              hoverStyle={modalStyles.modalCloseXHoverStyle}
              pressedStyle={modalStyles.modalCloseXPressedStyle}
              aria-label="Close">
              ×
            </LgmButton>
          </div>
        </div>

        {/* Collapsible Help Section */}
        {showHelp && (
          <div
            style={{
              backgroundColor: '#f0f9ff',
              border: `1px solid ${COLOR_INFO}`,
              borderRadius: '4px',
              padding: '16px',
              marginBottom: '16px',
            }}>
            <h3 style={{ marginTop: 0, fontSize: '14px', fontWeight: '600' }}>
              How to Merge {domainDisplayNamePlural}
            </h3>
            <ol
              style={{
                marginLeft: '20px',
                lineHeight: '1.6',
                fontSize: '14px',
              }}>
              <li>
                <strong>Select Target:</strong> Choose which{' '}
                {domainDisplayName.toLowerCase()} to merge into. Suggested
                similar names are shown first.
              </li>
              <li>
                <strong>Resolve Conflicts:</strong> Choose which name to keep as
                the canonical name. The source {domainDisplayName.toLowerCase()}{' '}
                will be marked inactive.
              </li>
              <li>
                <strong>Preview Changes:</strong> Review how many products will
                be affected and confirm the merge.
              </li>
              <li>
                <strong>Note:</strong> All products linked to the source will be
                automatically updated to reference the target. This action
                cannot be undone.
              </li>
            </ol>
          </div>
        )}

        {/* Modal Body */}
        <div style={modalStyles.modalBodyStyle}>
          {/* Step 1: Select Target */}
          {step === 'SELECT_TARGET' && (
            <>
              <h3 style={{ marginTop: 0 }}>
                Step 1: Select Target {domainDisplayName}
              </h3>
              <p style={{ color: GRAY_500, marginBottom: '16px' }}>
                Choose which {domainDisplayName.toLowerCase()} to merge "
                {sourceName}" into.
              </p>

              {/* Search Input */}
              <input
                type="text"
                aria-label={`Search ${domainDisplayNamePlural.toLowerCase()}`}
                placeholder={`Search ${domainDisplayNamePlural.toLowerCase()}...`}
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                style={{
                  ...modalStyles.floatingInputStyle,
                  marginBottom: isServerSearching ? '4px' : '16px',
                }}
              />
              {isServerSearching && (
                <p
                  style={{
                    color: GRAY_500,
                    fontSize: '12px',
                    margin: '0 0 12px',
                  }}>
                  Searching all {domainDisplayNamePlural.toLowerCase()}...
                </p>
              )}

              {/* Suggested Matches */}
              {filteredTargets.suggestions.length > 0 && (
                <div style={{ marginBottom: '24px' }}>
                  <h4
                    style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: GRAY_700,
                    }}>
                    Suggested Matches
                  </h4>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '8px',
                    }}>
                    {filteredTargets.suggestions.map(entity => (
                      <button
                        key={entity[entityIdField] as number}
                        onClick={() =>
                          handleTargetSelect(entity[entityIdField] as number)
                        }
                        style={{
                          padding: '12px',
                          border: `1px solid ${GRAY_300}`,
                          borderRadius: '4px',
                          backgroundColor: COLOR_WARNING_BG,
                          textAlign: 'left',
                          cursor: 'pointer',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          transition: 'all 0.2s ease-in-out',
                        }}
                        onMouseEnter={e =>
                          Object.assign(e.currentTarget.style, {
                            backgroundColor: '#fde68a',
                            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                          })
                        }
                        onMouseLeave={e =>
                          Object.assign(e.currentTarget.style, {
                            backgroundColor: COLOR_WARNING_BG,
                            boxShadow: 'none',
                          })
                        }>
                        <div>
                          <div style={{ fontWeight: '500' }}>
                            {entity[entityNameField] as string}
                          </div>
                          <div style={{ fontSize: '12px', color: GRAY_500 }}>
                            Similarity: {entity.similarity_score}%
                          </div>
                        </div>
                        <span style={{ color: COLOR_INFO, fontSize: '14px' }}>
                          Select →
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* All Other Targets */}
              {filteredTargets.others.length > 0 && (
                <div>
                  <h4
                    style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: GRAY_700,
                    }}>
                    {searchEntities && searchTerm.trim()
                      ? `Search Results — All ${domainDisplayNamePlural}`
                      : `All ${domainDisplayNamePlural}`}
                  </h4>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '8px',
                      maxHeight: '300px',
                      overflow: 'auto',
                    }}>
                    {filteredTargets.others.map(entity => (
                      <button
                        key={entity[entityIdField] as number}
                        onClick={() =>
                          handleTargetSelect(entity[entityIdField] as number)
                        }
                        style={{
                          padding: '12px',
                          border: `1px solid ${GRAY_300}`,
                          borderRadius: '4px',
                          backgroundColor: '#ffffff',
                          textAlign: 'left',
                          cursor: 'pointer',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          transition: 'all 0.2s ease-in-out',
                        }}
                        onMouseEnter={e =>
                          Object.assign(e.currentTarget.style, {
                            backgroundColor: GRAY_100,
                            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                          })
                        }
                        onMouseLeave={e =>
                          Object.assign(e.currentTarget.style, {
                            backgroundColor: '#ffffff',
                            boxShadow: 'none',
                          })
                        }>
                        <div style={{ fontWeight: '500' }}>
                          {entity[entityNameField] as string}
                        </div>
                        <span style={{ color: COLOR_INFO, fontSize: '14px' }}>
                          Select →
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {!isServerSearching &&
                filteredTargets.suggestions.length === 0 &&
                filteredTargets.others.length === 0 && (
                  <p
                    style={{
                      color: GRAY_400,
                      textAlign: 'center',
                      padding: '32px',
                    }}>
                    No {domainDisplayNamePlural.toLowerCase()} found. Try a
                    different search term.
                  </p>
                )}
            </>
          )}

          {/* Step 2: Resolve Conflicts */}
          {step === 'RESOLVE_CONFLICTS' && selectedTarget && (
            <>
              <h3 style={{ marginTop: 0 }}>Step 2: Resolve Conflicts</h3>
              <p style={{ color: GRAY_500, marginBottom: '16px' }}>
                Choose which {domainDisplayName.toLowerCase()} name to keep. The
                source will be merged into the target (marked inactive).
              </p>

              {/* Swap Button */}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  marginBottom: '16px',
                }}>
                <LgmButton
                  variant="contained"
                  color="primary"
                  onClick={handleSwapSourceTarget}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                  hoverStyle={{
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                  }}
                  pressedStyle={{
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                  }}
                  title="Swap source and target">
                  ⇄ Swap Source & Target
                </LgmButton>
              </div>

              {/* Side-by-side comparison */}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr auto 1fr',
                  gap: '16px',
                  marginBottom: '24px',
                  alignItems: 'center',
                }}>
                {/* Source (Left) */}
                <div
                  style={{
                    border: `2px solid ${COLOR_WARNING}`,
                    borderRadius: '8px',
                    padding: '16px',
                    backgroundColor: '#fffbeb',
                  }}>
                  <div
                    style={{
                      fontSize: '12px',
                      fontWeight: '600',
                      color: COLOR_WARNING_TEXT,
                      marginBottom: '8px',
                    }}>
                    SOURCE (Will become inactive)
                  </div>
                  <div style={{ marginBottom: '12px' }}>
                    <label
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                      }}>
                      <input
                        type="radio"
                        name="canonical_name"
                        checked={selectedCanonicalName === 'source'}
                        onChange={() => setSelectedCanonicalName('source')}
                      />
                      <span style={{ fontWeight: '500', fontSize: '16px' }}>
                        {sourceName}
                      </span>
                    </label>
                  </div>
                  <div style={{ fontSize: '12px', color: GRAY_500 }}>
                    <div>ID: {sourceId}</div>
                    <div>
                      Created:{' '}
                      {new Date(sourceCreatedDate).toLocaleDateString()}
                    </div>
                  </div>
                </div>

                {/* Arrow */}
                <div style={{ fontSize: '24px', color: GRAY_500 }}>→</div>

                {/* Target (Right) */}
                <div
                  style={{
                    border: '2px solid #10b981',
                    borderRadius: '8px',
                    padding: '16px',
                    backgroundColor: '#ecfdf5',
                  }}>
                  <div
                    style={{
                      fontSize: '12px',
                      fontWeight: '600',
                      color: '#065f46',
                      marginBottom: '8px',
                    }}>
                    TARGET (Canonical)
                  </div>
                  <div style={{ marginBottom: '12px' }}>
                    <label
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                      }}>
                      <input
                        type="radio"
                        name="canonical_name"
                        checked={selectedCanonicalName === 'target'}
                        onChange={() => setSelectedCanonicalName('target')}
                      />
                      <span style={{ fontWeight: '500', fontSize: '16px' }}>
                        {selectedTarget[entityNameField] as string}
                      </span>
                    </label>
                  </div>
                  <div style={{ fontSize: '12px', color: GRAY_500 }}>
                    <div>ID: {selectedTarget[entityIdField] as number}</div>
                    <div>
                      Created:{' '}
                      {new Date(
                        selectedTarget[entityCreatedDateField] as string
                      ).toLocaleDateString()}
                    </div>
                  </div>
                </div>
              </div>

              {/* Preview Data */}
              {previewQuery.status === 'success' && previewData && (
                <div
                  style={{
                    backgroundColor: GRAY_100,
                    padding: '16px',
                    borderRadius: '4px',
                    marginBottom: '16px',
                  }}>
                  <h4
                    style={{
                      marginTop: 0,
                      fontSize: '14px',
                      fontWeight: '600',
                    }}>
                    Impact Preview
                  </h4>
                  <div style={{ fontSize: '14px', lineHeight: '1.6' }}>
                    {/* Show facilities if manufacturer domain */}
                    {previewData.source_facility_count !== undefined && (
                      <>
                        <div
                          style={{
                            fontWeight: '600',
                            marginTop: '8px',
                            marginBottom: '4px',
                          }}>
                          Facilities:
                        </div>
                        <div>
                          • Source has{' '}
                          <strong>{previewData.source_facility_count}</strong>{' '}
                          facility/facilities
                        </div>
                        <div>
                          • Target has{' '}
                          <strong>{previewData.target_facility_count}</strong>{' '}
                          facility/facilities
                        </div>
                        <div>
                          • After merge: Total of{' '}
                          <strong>
                            {previewData.total_facilities_after_merge}
                          </strong>{' '}
                          facility/facilities
                        </div>
                      </>
                    )}
                    {/* Show products */}
                    <div
                      style={{
                        fontWeight: '600',
                        marginTop:
                          previewData.source_facility_count !== undefined
                            ? '12px'
                            : '8px',
                        marginBottom: '4px',
                      }}>
                      {previewData.source_facility_count !== undefined
                        ? 'Products:'
                        : ''}
                    </div>
                    <div>
                      • Source has{' '}
                      <strong>
                        {previewData.source_product_count ??
                          previewData.source_related_count}
                      </strong>{' '}
                      product(s)
                    </div>
                    <div>
                      • Target has{' '}
                      <strong>
                        {previewData.target_product_count ??
                          previewData.target_related_count}
                      </strong>{' '}
                      product(s)
                    </div>
                    <div>
                      • After merge: Total of{' '}
                      <strong>
                        {previewData.total_products_after_merge ??
                          previewData.total_related_after_merge}
                      </strong>{' '}
                      product(s)
                    </div>
                    {(previewData.overlapping_related_count > 0 ||
                      (previewData.overlapping_products ?? 0) > 0 ||
                      (previewData.overlapping_facilities ?? 0) > 0) && (
                      <div style={{ color: COLOR_WARNING, marginTop: '8px' }}>
                        ⚠️ {previewData.overlap_warning}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Domain-specific Conflict Resolution */}
              {previewQuery.status === 'success' &&
                previewData &&
                renderConflicts &&
                renderConflicts(
                  previewData,
                  resolvedFields,
                  handleFieldResolve
                )}

              <div style={{ marginBottom: '16px' }}>
                <label
                  style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '500',
                    marginBottom: '8px',
                    color: GRAY_700,
                  }}>
                  Merge Reason (Optional)
                </label>
                <textarea
                  value={mergeReason}
                  onChange={e => setMergeReason(e.target.value)}
                  placeholder={`Explain why these ${domainDisplayNamePlural.toLowerCase()} are being merged...`}
                  rows={3}
                  style={{
                    ...modalStyles.floatingTextareaStyle,
                    marginBottom: 0,
                  }}
                />
              </div>
            </>
          )}

          {/* Step 3: Confirm */}
          {step === 'CONFIRM' && selectedTarget && (
            <>
              <h3 style={{ marginTop: 0 }}>Step 3: Confirm Merge</h3>

              <div style={modalStyles.confirmStepWarningContainer}>
                <h4 style={modalStyles.confirmStepWarningTitle}>
                  ⚠️ Warning: This action cannot be undone
                </h4>
                <div style={modalStyles.confirmStepWarningText}>
                  The following changes will be made:
                </div>
              </div>

              <div style={modalStyles.confirmStepChangesContainer}>
                <div style={modalStyles.confirmStepChangesText}>
                  <div>
                    1. <strong>"{sourceName}"</strong> will be marked as
                    inactive
                  </div>
                  <div>
                    2. Canonical name will be:{' '}
                    <strong>
                      "
                      {selectedCanonicalName === 'source'
                        ? sourceName
                        : (selectedTarget[entityNameField] as string)}
                      "
                    </strong>
                  </div>
                  <div>
                    3. All products will be linked to{' '}
                    <strong>
                      "{selectedTarget[entityNameField] as string}"
                    </strong>
                  </div>
                  {previewData && (
                    <div>
                      4. Total products affected:{' '}
                      <strong>{previewData.total_related_after_merge}</strong>
                    </div>
                  )}
                  <div>
                    5. An alias entry will be created for "{sourceName}"
                  </div>
                  <div>6. All changes will be logged in the Audit Log</div>
                </div>
              </div>

              {mergeMutation.status === 'error' && (
                <div
                  style={{
                    marginTop: '16px',
                    padding: '12px',
                    backgroundColor: '#fef2f2',
                    border: `1px solid ${COLOR_DANGER}`,
                    borderRadius: '4px',
                    color: COLOR_DANGER_TEXT,
                  }}>
                  Error:{' '}
                  {(mergeMutation.error as Error)?.message || 'Merge failed'}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer - Step 1 */}
        {step === 'SELECT_TARGET' && (
          <div style={modalStyles.modalFooterStyle}>
            <LgmButton variant="contained" color="secondary" onClick={onClose}>
              Cancel
            </LgmButton>
          </div>
        )}

        {/* Footer - Step 2 */}
        {step === 'RESOLVE_CONFLICTS' && (
          <div style={modalStyles.modalFooterStyle}>
            <LgmButton
              variant="contained"
              color="secondary"
              onClick={handleBack}>
              ← Back
            </LgmButton>
            <LgmButton
              variant="contained"
              color="save"
              onClick={handleProceedToConfirm}>
              Next: Review →
            </LgmButton>
          </div>
        )}

        {/* Footer - Step 3 */}
        {step === 'CONFIRM' && (
          <div style={modalStyles.modalFooterStyle}>
            <LgmButton
              variant="contained"
              color="secondary"
              onClick={handleBack}>
              ← Back
            </LgmButton>
            <LgmButton
              variant="contained"
              color="danger"
              onClick={handleExecuteMerge}
              disabled={mergeMutation.status === 'loading'}
              hoverStyle={{
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
              }}
              pressedStyle={{
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
              }}>
              {mergeMutation.status === 'loading'
                ? 'Merging...'
                : 'Confirm Merge'}
            </LgmButton>
          </div>
        )}
      </div>
    </div>
  );
}

export default GenericMergeModal;
